import React, { useState, useEffect } from 'react';
import BlueButton from '../../components/BlueButton';
import { useStateContext } from '../../context/StateContext';

const ClaudeAi = ({
  stream = true, 
  systemPrompt = null, 
  model='claude-3-5-sonnet-20241022', 
  userPrompt = null, 
  autoSubmit = false,
  maxNTokens = false,
  temperature = 0.7
}) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [copyStatus, setCopyStatus] = useState({});
  const [lastSubmittedPrompt, setLastSubmittedPrompt] = useState('');
  const { apiUrl, hideTestButtons } = useStateContext();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const promptToSubmit = autoSubmit ? userPrompt : input;
    if (!promptToSubmit?.trim() || isLoading) return;

    setError(null);
    const newMessages = [...messages, { role: 'user', content: promptToSubmit }];
    setMessages(newMessages);
    if (!autoSubmit) setInput('');
    setIsLoading(true);
    setStreamingMessage('');

    try {
      if (stream) {
        // Set up event source for streaming with POST request
        const response = await fetch(`${apiUrl}/anthropic/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            messages: newMessages,
            model,
            stream: true,
            maxTokens: maxNTokens ? 4096 : 1024,
            temperature,
            ...(systemPrompt && { systemPrompt })
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to connect to chat endpoint');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let fullResponse = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          
          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');
          
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                if (data.content) {
                  fullResponse += data.content;
                  setStreamingMessage(fullResponse);
                }
              } catch (e) {
                process.env.NODE_ENV === "development" && console.error('Error parsing SSE data:', e);
              }
            }
          }
        }

        setMessages([...newMessages, { 
          role: 'assistant', 
          content: fullResponse 
        }]);

      } else {
        // Non-streaming implementation
        const response = await fetch(`${apiUrl}/anthropic/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            messages: newMessages,
            model,
            stream: false,
            maxTokens: maxNTokens ? 4096 : 1024,
            temperature,
            ...(systemPrompt && { systemPrompt })
          }),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Failed to get response from Claude');
        }

        const data = await response.json();
        setMessages([...newMessages, { 
          role: 'assistant', 
          content: data.response 
        }]);
      }
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error calling Claude:', error);
      if (error.status === 401) {
        setError('Invalid API key. Please check your configuration.');
      } else if (error.status === 429) {
        setError('Rate limit exceeded. Please try again later.');
      } else if (error.status === 500) {
        setError('Claude API service error. Please try again later.');
      } else if (error.message === 'Invalid response from Claude API') {
        setError('Received an invalid response from Claude. Please try again.');
      } else {
        setError(`Error: ${error.message || 'An unknown error occurred'}`);
      }
    } finally {
      setIsLoading(false);
      setStreamingMessage('');
    }
  };

  useEffect(() => {
    if (userPrompt) {
      // Only set input if not in autoSubmit mode
      if (!autoSubmit) {
        setInput(userPrompt);
      }
      
      if (autoSubmit && userPrompt !== lastSubmittedPrompt && !isLoading) {
        const submitEvent = new Event('submit', { cancelable: true });
        handleSubmit(submitEvent);
        setLastSubmittedPrompt(userPrompt);
      }
    }
  }, [userPrompt, autoSubmit, handleSubmit, lastSubmittedPrompt, isLoading]);

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyStatus(prev => ({ ...prev, [id]: 'Copied!' }));
      setTimeout(() => {
        setCopyStatus(prev => {
          const newStatus = { ...prev };
          delete newStatus[id];
          return newStatus;
        });
      }, 2000);
    } catch (err) {
      process.env.NODE_ENV === "development" && console.error('Failed to copy text: ', err);
      setCopyStatus(prev => ({ ...prev, [id]: 'Failed to copy' }));
      setTimeout(() => {
        setCopyStatus(prev => {
          const newStatus = { ...prev };
          delete newStatus[id];
          return newStatus;
        });
      }, 2000);
    }
  };

  const CopyButton = ({ text, id }) => (
    <button
      onClick={() => copyToClipboard(text, id)}
      style={{
        padding: '4px 8px',
        marginLeft: '8px',
        backgroundColor: '#f0f0f0',
        border: '1px solid #ddd',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '12px',
        color: '#666',
        display: 'flex',
        alignItems: 'center',
        transition: 'all 0.2s ease',
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.backgroundColor = '#e0e0e0';
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.backgroundColor = '#f0f0f0';
      }}
    >
      {copyStatus[id] || 'Copy'}
    </button>
  );

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <div style={{ 
      height: '76vh', 
      display: 'flex', 
      flexDirection: 'column',
      backgroundColor: '#f5f5f5'
    }}>
      {/* Messages Container */}
      <div style={{ 
        flex: 1, 
        overflowY: 'auto', 
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px'
      }}>
        {messages?.map((message, index) => (
          <div
            key={index}
            style={{
              display: 'flex',
              justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                maxWidth: '70%',
                padding: '12px 16px',
                borderRadius: '12px',
                backgroundColor: message.role === 'user' ? '#007bff' : '#ffffff',
                color: message.role === 'user' ? '#ffffff' : '#000000',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                whiteSpace: 'pre-wrap',
              }}
            >
              {message.content}
            </div>
            <CopyButton text={message.content} id={`message-${index}`} />
          </div>
        ))}
        {/* Streaming message display */}
        {streamingMessage && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                maxWidth: '70%',
                padding: '12px 16px',
                borderRadius: '12px',
                backgroundColor: '#ffffff',
                color: '#000000',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                whiteSpace: 'pre-wrap',
              }}
            >
              {streamingMessage}
            </div>
            <CopyButton text={streamingMessage} id="streaming" />
          </div>
        )}
        {isLoading && !streamingMessage && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'flex-start', 
            marginTop: '10px' 
          }}>
            <div style={{ 
              padding: '12px 16px',
              borderRadius: '12px',
              backgroundColor: '#ffffff',
              color: '#666',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}>
              Claude is thinking...
            </div>
          </div>
        )}
        {error && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            marginTop: '10px' 
          }}>
            <div style={{ 
              padding: '12px 16px',
              borderRadius: '12px',
              backgroundColor: '#fee2e2',
              color: '#dc2626',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}>
              {error}
            </div>
          </div>
        )}
      </div>

      {/* Input Form */}
      <form 
        onSubmit={handleSubmit}
        style={{ 
          padding: '20px',
          backgroundColor: '#ffffff',
          borderTop: '1px solid #ddd',
        }}
      >
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Write here... (Press Enter to send, Shift+Enter for new line)"
          disabled={isLoading}
          style={{
            width: '100%',
            padding: '12px',
            borderRadius: '8px',
            border: '1px solid #ddd',
            minHeight: '100px',
            resize: 'vertical',
            marginBottom: '12px',
            fontFamily: 'inherit',
            fontSize: '14px',
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <BlueButton style={{cursor: isLoading ? 'not-allowed' : 'pointer',}} disabled={isLoading} fn={handleSubmit} text= {isLoading ? 'Sending...' : 'Send'}/>
{/*           <button
            type="submit"
            disabled={isLoading}
            style={{
              padding: '10px 24px',
              backgroundColor: isLoading ? '#ccc' : '#007bff',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            {isLoading ? 'Sending...' : 'Send'}
          </button> */}
        </div>
      </form>
    </div>
  );
};

export default ClaudeAi;
